function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      ""
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute("style");
      deferredElement.setAttribute("style", "display: block;");
      window.setTimeout(() => {
        deferredElement.setAttribute("style", formerStyle);
      }, 0);
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

/* ===============================
   Shopify AJAX Variant Handler
   =============================== */
// Utility: Money Formatter (modern fallback)
function formatMoney(cents, currency = window.Shopify?.currency?.active || 'CHF') {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const amount = (cents / 100).toFixed(2);
  
  // Simple currency symbol mapping
  const currencySymbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CAD': '$',
    'AUD': '$',
    'CHF' : 'CHF'
  };
  
  const symbol = currencySymbols[currency] || '$';
  return `${symbol}${amount}`;
}

/* ===============================
   <variant-selects> Component
  =============================== */
class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      const selectedValues = this.selectedOptionValues;

      // Publish Shopify-style variantChange event
      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          event,
          target,
          selectedOptionValues: selectedValues,
        },
      });
    });
  }

  // Get currently selected option values (readable names)
  // Uses .selectedOptions for selects (reliable after DOM changes) and :checked for radios
  get selectedOptionValues() {
    const values = [];
    
    // Get values from select dropdowns using .selectedOptions (not the attribute)
    this.querySelectorAll('select').forEach((select) => {
      if (select.selectedOptions.length > 0) {
        values.push(select.selectedOptions[0].value);
      }
    });
    
    // Get values from radio inputs
    this.querySelectorAll('fieldset input[type="radio"]:checked, input[type="radio"]:checked').forEach((radio) => {
      values.push(radio.value);
    });
    
    return values;
  }

  // Get the currently selected variant based on all option selections
  get selectedVariant() {
    if (!productData) return null;
    
    const selectedOptions = this.selectedOptionValues;
    if (selectedOptions.length === 0) return null;

    return productData.variants.find((variant) => {
      return variant.options.every((opt, i) => opt === selectedOptions[i]);
    });
  }

  // 🧠 Normalize event target for both select & radio
  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  // Keep UI in sync (radio checked, select selected)
  updateSelectionMetadata({ target }) {
    const { value, tagName, type, name } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        ?.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');
    } else if (tagName === 'INPUT' && type === 'radio') {
      // Uncheck all radios in same group, check the clicked one
      const radios = this.querySelectorAll(`input[name="${name}"]`);
      radios.forEach((r) => {
        r.removeAttribute('checked');
        // Remove selected class from labels
        const label = r.nextElementSibling;
        if (label && label.classList.contains('card-swatch-label')) {
          label.classList.remove('selected');
        }
      });
      
      target.setAttribute('checked', 'checked');
      
      // Add selected class to current label
      const currentLabel = target.nextElementSibling;
      if (currentLabel && currentLabel.classList.contains('card-swatch-label')) {
        currentLabel.classList.add('selected');
      }

      // Update selected value text (optional)
      const selectedValueDisplay = target
        .closest(`.product-form__input`)
        ?.querySelector('[data-selected-value]');
      if (selectedValueDisplay) selectedValueDisplay.textContent = value;
    }
  }
}

customElements.define('variant-selects', VariantSelects);

/* ===============================
   Card Swatch Event Handler
   =============================== */
document.addEventListener('DOMContentLoaded', () => {
  let cardSwatchProcessed = new WeakSet(); // Track which events we've already processed
  
  // Handle card swatch change events directly - NO synthetic events to prevent loops
  document.addEventListener('change', (event) => {
    if (event.target.classList.contains('card-swatch-input') && !cardSwatchProcessed.has(event)) {
      // Mark this event as processed to prevent re-processing
      cardSwatchProcessed.add(event);     
      const variantSelects = event.target.closest('variant-selects');
      if (variantSelects) {
        
        // Get the selected variant based on all current selections
        const selectedVariant = variantSelects.selectedVariant;
        
        if (selectedVariant) {
          // Update images and display immediately - NO event dispatching
          updateProductImages(selectedVariant);          
          updateVariantDisplay(selectedVariant);          
          updateOptionAvailability();
          
          // Manually publish the pub/sub event (what VariantSelects.connectedCallback would do)
          // This ensures other listeners are notified without infinite loops
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              event,
              target: event.target,
              selectedOptionValues: variantSelects.selectedOptionValues,
            },
          });
        } 
      }
    }
  });

  // Handle clicks on card swatch items to ensure the radio input gets checked
  document.addEventListener('click', (event) => {
    let targetInput = null;
    
    if (event.target.classList.contains('card-swatch-input')) {
      targetInput = event.target;
    } else if (event.target.closest('.card-swatch-label')) {
      const label = event.target.closest('.card-swatch-label');
      targetInput = document.querySelector(`#${label.getAttribute('for')}`);
    } else if (event.target.closest('.card-swatch-item')) {
      targetInput = event.target.closest('.card-swatch-item').querySelector('.card-swatch-input');
    }
    
    if (targetInput && targetInput.classList.contains('card-swatch-input') && !targetInput.checked) {
      // Just check the input - the change event will fire naturally and be handled above
      targetInput.checked = true;
      // Manually trigger change event (this is safe because we track which events are processed)
      targetInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  // Handle label clicks for better UX
  document.addEventListener('click', (event) => {
    if (event.target.closest('.card-swatch-label')) {
      const label = event.target.closest('.card-swatch-label');
      const input = document.querySelector(`#${label.getAttribute('for')}`);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Force variant display update after swatch click (including images)
        setTimeout(() => {
          const variantSelects = document.querySelector('variant-selects');
          if (variantSelects) {
            const selectedVariant = variantSelects.selectedVariant;
            if (selectedVariant) {
              updateVariantDisplay(selectedVariant);
              updateOptionAvailability();
            }
          }
        }, 50);
      }
    }
  });

  // Initialize availability on page load
  setTimeout(() => {
    updateOptionAvailability();
    // Set initial variant if one is selected
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      const selectedVariant = variantSelects.selectedVariant;
      if (selectedVariant) {
        updateVariantDisplay(selectedVariant);
      }
    }
    
    // Set up price element monitoring to prevent other scripts from interfering
    setupPriceMonitoring();
  }, 100);

  // Fix add to cart button after form submission
  document.addEventListener('submit', (event) => {
    if (event.target.closest('product-form')) {
      const submitButton = event.target.querySelector('[type="submit"]');
      if (submitButton) {
        // Re-enable button after a delay (in case of errors)
        setTimeout(() => {
          if (!submitButton.classList.contains('loading')) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.disabled = false;
          }
        }, 3000); // 3 second timeout
      }
    }
  });

  // Listen for cart update events to re-enable button and refresh price display
  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      const addToCartBtn = document.querySelector('[name="add"], .product-form__cart-submit, button[type="submit"]');
      if (addToCartBtn && !addToCartBtn.classList.contains('loading')) {
        addToCartBtn.removeAttribute('aria-disabled');
        addToCartBtn.disabled = false;
        addToCartBtn.classList.remove('disabled');
      }
      
      // Refresh current variant display after cart update
      setTimeout(() => {
        const variantSelects = document.querySelector('variant-selects');
        if (variantSelects) {
          const selectedVariant = variantSelects.selectedVariant;
          if (selectedVariant) {
            updateProductPrice(selectedVariant);
          }
        }
      }, 100);
    });
    
    // Also listen for cart errors to ensure price display remains functional
    subscribe(PUB_SUB_EVENTS.cartError, () => {
      setTimeout(() => {
        const variantSelects = document.querySelector('variant-selects');
        if (variantSelects) {
          const selectedVariant = variantSelects.selectedVariant;
          if (selectedVariant) {
            updateProductPrice(selectedVariant);
          }
        }
      }, 100);
    });
  }
});

/* ===============================
   Global Variant Change Listener
   =============================== */

// Get product JSON (from theme section)
const productJson = document.getElementById('ProductJson-main-product');
const productData = productJson ? JSON.parse(productJson.textContent) : null;

subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
  const { selectedOptionValues } = data;
  
  if (!productData || !selectedOptionValues.length) {
    return;
  }

  // Find matching variant
  const selectedVariant = productData.variants.find((variant) => {
    return variant.options.every((opt, i) => opt === selectedOptionValues[i]);
  });


  if (selectedVariant) {
    updateVariantDisplay(selectedVariant);
    updateOptionAvailability();
  }
});

/* ===============================
   Update Product Display Instantly
   =============================== */
function updateVariantDisplay(variant) {
    
  // Force refresh price display by clearing any cached states
  updateProductPrice(variant);

  // Update Featured Image using improved method
  updateProductImages(variant);
  
  // Try product-form custom element first (most reliable)
  const productFormElement = document.querySelector('product-form');
  if (productFormElement && productFormElement.variantIdInput) {
    productFormElement.variantIdInput.value = variant.id;
    productFormElement.variantIdInput.setAttribute('value', variant.id);
    //productFormElement.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
  } else {
    // Fallback: search for variant inputs in order of specificity
    const variantInputSelectors = [
      'product-form input[name="id"]',
      'form[action*="cart/add"] input[name="id"]',
      'form input[name="id"]',
      'input[name="id"]'
    ];
    
    let variantInputUpdated = false;
    
    for (const selector of variantInputSelectors) {
      const variantInputs = document.querySelectorAll(selector);
      if (variantInputs.length > 0) {
        variantInputs.forEach(input => {
          input.value = variant.id;
          input.setAttribute('value', variant.id);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        variantInputUpdated = true;
        break;
      }
    }
    
    if (!variantInputUpdated) {
      const allForms = document.querySelectorAll('form');
      allForms.forEach((form, index) => {
      });
    }
  }

  // Update Add to Cart button - try multiple selectors
  const addToCartSelectors = [
    '[name="add"]',
    '.product-form__cart-submit',
    '.btn--add-to-cart',
    '[data-add-to-cart]',
    'button[type="submit"]'
  ];
  
  for (const selector of addToCartSelectors) {
    const addToCartBtn = document.querySelector(selector);
    if (addToCartBtn) {
      // Clear all disabled states
      addToCartBtn.removeAttribute('disabled');
      addToCartBtn.removeAttribute('aria-disabled');
      addToCartBtn.classList.remove('disabled', 'loading');
      
      if (variant.available) {
        // Enable the button
        addToCartBtn.disabled = false;
        addToCartBtn.classList.remove('disabled');
        
        // Update button text
        const buttonText = addToCartBtn.querySelector('span') || addToCartBtn;
        const originalText = addToCartBtn.getAttribute('data-add-text') || 
                           buttonText.getAttribute('data-add-to-cart-text') || 
                           'Add to cart';
        buttonText.textContent = originalText;
        
        // Show any hidden text spans
        const hiddenSpans = addToCartBtn.querySelectorAll('span.hidden');
        hiddenSpans.forEach(span => span.classList.remove('hidden'));
        
        // Hide sold out message if present
        const soldOutMessage = addToCartBtn.querySelector('.sold-out-message');
        if (soldOutMessage) soldOutMessage.classList.add('hidden');
        
      } else {
        // Disable for sold out
        addToCartBtn.disabled = true;
        addToCartBtn.setAttribute('aria-disabled', 'true');
        addToCartBtn.classList.add('disabled');
        
        // Update button text
        const buttonText = addToCartBtn.querySelector('span') || addToCartBtn;
        const unavailableText = addToCartBtn.getAttribute('data-unavailable-text') || 
                               buttonText.getAttribute('data-sold-out-text') || 
                               'Sold out';
        buttonText.textContent = unavailableText;
      }
      break;
    }
  }

  // also update product form component if it exists
  const productForm = document.querySelector('product-form');
  if (productForm && productForm.toggleSubmitButton) {
    if (variant.available) {
      productForm.toggleSubmitButton(false);
    } else {
      productForm.toggleSubmitButton(true, 'Sold out');
    }
  }

  // Update SKU if present
  const skuContainer = document.querySelector('[data-sku]');
  if (skuContainer) {
    if (variant.sku) {
      skuContainer.textContent = variant.sku;
      skuContainer.style.display = 'block';
    } else {
      skuContainer.style.display = 'none';
    }
  }

  // Dispatch custom event for other components
  document.dispatchEvent(new CustomEvent('variant:changed', {
    detail: {
      variant: variant,
      variantId: variant.id
    }
  }));
}

/* ===============================
   Enhanced Price Update Functions
   =============================== */
function updateProductPrice(variant) {
  
  // Find the PRODUCT PAGE price container specifically (not cart drawer)
  // Use the section ID wrapper to ensure we target the right element
  let priceWrapper = document.querySelector('[id^="price-"][id$="__main"]');
  let priceContainer = priceWrapper?.querySelector('.price') || document.querySelector('product-info .price');
  
  // Fallback: if no wrapper found, try product-info context first
  if (!priceContainer) {
    const productInfo = document.querySelector('product-info');
    priceContainer = productInfo?.querySelector('.price');
  }
  
  // Final fallback
  if (!priceContainer) {
    priceContainer = document.querySelector('.price');
  }
  
  if (!priceContainer) {
    return;
  }
  
  // Try multiple selector strategies to find price elements
  const regularPriceDiv = priceContainer.querySelector('.price__container > .price__regular') 
                       || priceContainer.querySelector('.price__regular');
  const salePriceDiv = priceContainer.querySelector('.price__container > .price__sale')
                    || priceContainer.querySelector('.price__sale');
  
  // Find price spans - try multiple selectors
  const regularPriceSpan = regularPriceDiv?.querySelector('.price-item--regular:not(s):not(s .price-item--regular)')
                        || regularPriceDiv?.querySelector('span.price-item--regular');
  const salePriceSpan = salePriceDiv?.querySelector('.price-item--sale')
                     || salePriceDiv?.querySelector('.price-item--last');
  
  
  // Find compare price spans in both sections
  const saleComparePriceSpan = salePriceDiv?.querySelector('s .price-item--regular, s.price-item--regular');
  const regularComparePriceSpan = regularPriceDiv?.querySelector('s .price-item--regular, s.price-item--regular');
  
  // Ensure prices are numbers and handle both cents and dollar formats
  const normalizePrice = (price) => {
    if (!price) return 0;
    const numPrice = parseFloat(price);
    // If the price seems to be in dollars (less than 100), convert to cents
    // If it's already in cents (100 or more), keep as is
    return numPrice < 100 ? numPrice * 100 : numPrice;
  };
  
  const normalizedPrice = normalizePrice(variant.price);
  const normalizedComparePrice = normalizePrice(variant.compare_at_price);
    
  // Check if product is on sale
  const isOnSale = normalizedComparePrice && normalizedComparePrice > normalizedPrice;
  
  // Update ALL price spans FIRST to ensure consistency
  const formattedCurrentPrice = formatMoney(normalizedPrice);
  const formattedComparePrice = normalizedComparePrice ? formatMoney(normalizedComparePrice) : null;
  
  
  // Update regular price span (always update to current price)
  if (regularPriceSpan) {
    regularPriceSpan.textContent = formattedCurrentPrice;
  }
  
  // Update sale price span (always update to current price)
  if (salePriceSpan) {
    salePriceSpan.textContent = formattedCurrentPrice;
  }
  
  
  // Update compare price spans if available
  if (formattedComparePrice) {
    if (saleComparePriceSpan) {
      saleComparePriceSpan.textContent = formattedComparePrice;
    }
    if (regularComparePriceSpan) {
      regularComparePriceSpan.textContent = formattedComparePrice;
    }
  }
  
  // Update price container classes to match sale state
  if (isOnSale) {
    priceContainer.classList.add('price--on-sale');
    priceContainer.classList.remove('price--no-sale');
  } else {
    priceContainer.classList.remove('price--on-sale');
    priceContainer.classList.add('price--no-sale');
  }
  
  if (isOnSale) {
    
    // Hide regular price div
    if (regularPriceDiv) {
      regularPriceDiv.classList.add('visibility-hidden');
      regularPriceDiv.style.display = 'none';
    }
    
    // Show sale price div with proper structure
    if (salePriceDiv) {
      salePriceDiv.classList.remove('visibility-hidden');
      salePriceDiv.style.display = 'flex';
      
      // Ensure sale price span is visible (already updated above)
      if (salePriceSpan) {
        salePriceSpan.style.display = '';
      }
      
      // Ensure compare price is visible in sale section
      if (saleComparePriceSpan && formattedComparePrice) {
        const parentSpan = saleComparePriceSpan.closest('span');
        if (parentSpan) {
          parentSpan.style.display = '';
        }
      }
    }     
  } else {
    // NOT on sale - show regular price, hide sale price
    
    // Show regular price div
    if (regularPriceDiv) {
      regularPriceDiv.classList.remove('visibility-hidden');
      regularPriceDiv.style.display = '';
    }
    
    // Hide sale price div
    if (salePriceDiv) {
      salePriceDiv.classList.add('visibility-hidden');
      salePriceDiv.style.display = 'none';
    }
  }
}
/* ===============================
   Price Monitoring System
   =============================== */
function setupPriceMonitoring() {
  let lastSelectedVariant = null;
  
  // Monitor price container for changes
  const priceContainer = document.querySelector('.price');
  if (priceContainer) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || 
            (mutation.type === 'attributes' && mutation.attributeName === 'style')) {
          
          // Check if we have a selected variant and prices might be out of sync
          const variantSelects = document.querySelector('variant-selects');
          if (variantSelects) {
            const selectedVariant = variantSelects.selectedVariant;
            if (selectedVariant && selectedVariant !== lastSelectedVariant) {
              lastSelectedVariant = selectedVariant;
              
              // Delay update to avoid conflicts
              setTimeout(() => {
                updateProductPrice(selectedVariant);
              }, 100);
            }
          }
        }
      });
    });
    
    observer.observe(priceContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }
  
  // Periodic refresh to ensure prices stay in sync (every 2 seconds)
  setInterval(() => {
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      const selectedVariant = variantSelects.selectedVariant;
      if (selectedVariant && selectedVariant !== lastSelectedVariant) {
        lastSelectedVariant = selectedVariant;
        updateProductPrice(selectedVariant);
      }
    }
  }, 2000);
  
  // Also refresh prices when page becomes visible (user switches back to tab)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      setTimeout(() => {
        const variantSelects = document.querySelector('variant-selects');
        if (variantSelects) {
          const selectedVariant = variantSelects.selectedVariant;
          if (selectedVariant) {
            updateProductPrice(selectedVariant);
          }
        }
      }, 200);
    }
  });
}

/* ===============================
   Update Product Images with Gallery Support
   =============================== */
function updateProductImages(variant) {  
  if (!variant || !variant.featured_media) {
    return;
  }
  
  const variantImageId = variant.featured_media.id.toString();
  
  // Find the gallery container
  const gallery = document.querySelector('slider-component[id^="GalleryViewer"]');
  if (!gallery) {
    return;
  }
    
  // Find all media items (li elements with data-media-id)
  const allMediaItems = gallery.querySelectorAll('li[data-media-id]');
  
  let mediaFound = false;
  
  // Loop through all media items
  allMediaItems.forEach((mediaItem, index) => {
    const dataMediaId = mediaItem.getAttribute('data-media-id');
    
    // Extract the media ID from data-media-id (format: "template--xxx__main-MEDIAID")
    // We need to get the part after the last hyphen
    const mediaIdPart = dataMediaId ? dataMediaId.split('-').pop() : '';
    // Check if this media item matches our variant image ID
    if (mediaIdPart === variantImageId) {      
      // Remove is-active from all media items first
      allMediaItems.forEach(item => {
        item.classList.remove('is-active');
      });
      
      // Add is-active to the matching item
      mediaItem.classList.add('is-active');
      mediaFound = true;
    }
  });
}

/* ===============================
   Update Slider Components
   =============================== */
function updateSliderComponents(targetMediaId) {
  
  // Update main gallery slider (slider-mobile-gutter)
  const galleryViewer = document.querySelector('#GalleryViewer, [id^="GalleryViewer"]');
  if (galleryViewer) {
    // Ensure the gallery viewer is visible
    galleryViewer.style.display = '';
    galleryViewer.style.visibility = 'visible';
    galleryViewer.classList.remove('hidden');
    
    // Find and activate the correct slide
    const targetSlide = galleryViewer.querySelector(`[data-media-id="${targetMediaId}"]`);
    if (targetSlide) {
      // Remove active class from all slides
      const allSlides = galleryViewer.querySelectorAll('.slider__slide');
      allSlides.forEach(slide => {
        slide.classList.remove('is-active');
        // Don't hide slides - let slider component handle visibility
      });
      
      // Activate the target slide
      targetSlide.classList.add('is-active');
      
      // Ensure the slide and its image are visible
      targetSlide.style.display = '';
      targetSlide.style.visibility = 'visible';
      
      const slideImg = targetSlide.querySelector('img');
      if (slideImg) {
        slideImg.style.display = '';
        slideImg.style.visibility = 'visible';
        slideImg.style.opacity = '1';
      }
      
      // Update slider component position if it has the method
      const sliderComponent = galleryViewer.closest('slider-component');
      if (sliderComponent) {
        // Try to scroll to the correct slide
        const slideIndex = Array.from(allSlides).indexOf(targetSlide);
        if (slideIndex !== -1 && sliderComponent.setSlidePosition) {
          try {
            sliderComponent.setSlidePosition(slideIndex * (sliderComponent.sliderItemOffset || 0));
          } catch (error) {
            console.log(error.message);
          }
        }
      }
    }
  }
  
  // Update thumbnail slider
  const thumbnailSlider = document.querySelector('#GalleryThumbnails, [id^="GalleryThumbnails"]');
  if (thumbnailSlider) {
    // Ensure thumbnail slider is visible
    thumbnailSlider.style.display = '';
    thumbnailSlider.style.visibility = 'visible';
    thumbnailSlider.classList.remove('hidden');
    
    // Find and activate the correct thumbnail
    const targetThumbnail = thumbnailSlider.querySelector(`[data-target="${targetMediaId}"]`);
    if (targetThumbnail) {
      // Remove active state from all thumbnails
      const allThumbnails = thumbnailSlider.querySelectorAll('button, .thumbnail-list__item');
      allThumbnails.forEach(thumb => {
        thumb.classList.remove('is-active');
        thumb.removeAttribute('aria-current');
        if (thumb.tagName === 'BUTTON') {
          thumb.setAttribute('tabindex', '-1');
        }
      });
      
      // Activate the target thumbnail
      targetThumbnail.classList.add('is-active');
      targetThumbnail.setAttribute('aria-current', 'true');
      if (targetThumbnail.tagName === 'BUTTON') {
        targetThumbnail.removeAttribute('tabindex');
      }
      
    }
  }
  
  // Update any other slider-mobile-gutter elements
  const allSliders = document.querySelectorAll('.slider-mobile-gutter');
  allSliders.forEach(slider => {
    // Make sure slider is visible
    slider.style.display = '';
    slider.style.visibility = 'visible';
    slider.classList.remove('hidden');
    
    // Look for slides with matching media ID
    const matchingSlide = slider.querySelector(`[data-media-id="${targetMediaId}"]`);
    if (matchingSlide) {
      // Remove active from all slides in this slider
      const slides = slider.querySelectorAll('.slider__slide');
      slides.forEach(slide => slide.classList.remove('is-active'));
      
      // Activate matching slide
      matchingSlide.classList.add('is-active');
      matchingSlide.style.display = '';
      matchingSlide.style.visibility = 'visible';
      
      const slideImg = matchingSlide.querySelector('img');
      if (slideImg) {
        slideImg.style.display = '';
        slideImg.style.visibility = 'visible';
        slideImg.style.opacity = '1';
      }
    }
  });
}

/* ===============================
   Update Option Availability
   =============================== */
function updateOptionAvailability() {
  if (!productData) return;

  const variantSelects = document.querySelector('variant-selects');
  if (!variantSelects) return;

  // Get currently selected values
  const selectedOptions = variantSelects.selectedOptionValues;

  // Check each option input for availability
  const allInputs = variantSelects.querySelectorAll('.card-swatch-input');
  
  allInputs.forEach(input => {
    const optionPosition = parseInt(input.dataset.optionPosition) - 1;
    const optionValue = input.dataset.optionValue;
    
    // Create a test combination with this option value
    const testOptions = [...selectedOptions];
    testOptions[optionPosition] = optionValue;
    
    // Check if any variant matches this combination
    const hasAvailableVariant = productData.variants.some(variant => {
      const matches = variant.options.every((opt, i) => {
        return i === optionPosition ? opt === optionValue : (testOptions[i] ? opt === testOptions[i] : true);
      });
      return matches && variant.available;
    });
    
    // Update input state
    input.disabled = !hasAvailableVariant;
    
    // Add visual feedback
    const label = input.nextElementSibling;
    if (label) {
      label.classList.toggle('unavailable', !hasAvailableVariant);
    }
  });
}


class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      event.target.setAttribute("value", inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance";

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }
}

class ModalSlider extends HTMLElement {
  constructor() {
    super();

    this.swiperLarge = this.querySelector(".swiper_large");
    this.swiperThumb = this.querySelector(".swiper_thumb");

    this.navPrevThumb = this.querySelector(".gallery-button-prev");
    this.navNextThumb = this.querySelector(".gallery-button-next");

    this.init();
    const resizeObserver = new ResizeObserver((entries) => this.init());
    resizeObserver.observe(this);
    // Update both sliders after initialization
    this.sliderThumb.update();
    this.sliderLarge.update();

    // Fix initial spacing
    this.sliderThumb.translateTo(0, 0, false);
  }

  init() {
    this.sliderThumb = new Swiper(this.swiperThumb, {
      loop: false,
      speed: 700,
      spaceBetween: 12,
      watchSlidesProgress: true,
      mousewheel: true,
      navigation: {
        nextEl: this.navNextThumb,
        prevEl: this.navPrevThumb,
      },
      breakpoints: {
        320: {
          slidesPerView: 3,
          direction: "horizontal",
        },
        480: {
          slidesPerView: 3,
          direction: "horizontal",
        },
        750: {
          slidesPerView: 3,
          direction: "horizontal",
        },
        800: {
          slidesPerView: 6,
          direction: "horizontal",
          spaceBetween: 12,
        },
        1024: {
          slidesPerView: 6,
          spaceBetween: 12,
          direction: "horizontal",
        },
        1366: {
          slidesPerView: 6,
          spaceBetween: 12,
          direction: "horizontal",
        },
        1400: {
          slidesPerView: 6,
          spaceBetween: 24,
          direction: "horizontal",
        },
      },
      on: {
        scroll: (swiper) => {
          // Sync with large slider on scroll
          if (this.sliderLarge) {
            this.sliderLarge.slideTo(swiper.activeIndex);
          }
        },
      },
    });

    this.sliderLarge = new Swiper(this.swiperLarge, {
      loop: false,
      speed: 700,
      slidesPerView: 1,
      spaceBetween: 0,
      thumbs: {
        swiper: this.sliderThumb,
      },
      on: {
        slideChange: () => {
          const sliderVideos = this.querySelectorAll(".slide_has_video");
          if (sliderVideos.length > 0) {
            window.pauseAllMedia();
          }
        },
      },
    });
  }
}

if (!customElements.get("modal-slider")) {
  customElements.define("modal-slider", ModalSlider);
}